import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, Package, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { getCategories, getSubcategories, getProducts } from '../services/api';
import { motion } from 'framer-motion';

const Category = () => {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSub, setActiveSub] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 24;

  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        const [catRes, subRes] = await Promise.all([
          getCategories(),
          getSubcategories(categoryId),
        ]);
        const found = catRes.data.find(c => c.id === categoryId);
        setCategory(found || null);
        setSubcategories(subRes.data || []);

        // Fetch ALL products in this category (the API caps each call at 50/limit),
        // paging through until we've collected every product so the website isn't
        // silently capped at 50 items.
        const BATCH = 200;
        let all = [];
        let skip = 0;
        // Guard against runaway loops — 100 batches = 20k products max.
        for (let i = 0; i < 100; i++) {
          const res = await getProducts({ category_id: categoryId, skip, limit: BATCH });
          const batch = res.data?.products || [];
          all = all.concat(batch);
          const total = res.data?.total ?? all.length;
          skip += BATCH;
          if (batch.length < BATCH || all.length >= total) break;
        }
        setCategoryProducts(all);
      } catch {
        setCategory(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryData();
  }, [categoryId]);

  // Derived list: subcategory filter + free-text search
  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return categoryProducts.filter(p => {
      if (activeSub && p.subcategory_id !== activeSub) return false;
      if (!term) return true;
      const hay = `${p.name} ${p.description || ''} ${p.sku || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [categoryProducts, activeSub, searchTerm]);

  // Reset to first page whenever the filtered result set changes.
  useEffect(() => { setPage(1); }, [activeSub, searchTerm, categoryProducts]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const pagedProducts = useMemo(
    () => filteredProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredProducts, page]
  );

  // Autosuggest pool (max 6 matches from category products)
  const suggestions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length < 2) return [];
    return categoryProducts
      .filter(p => p.name.toLowerCase().includes(term) || (p.sku || '').toLowerCase().includes(term))
      .slice(0, 6);
  }, [categoryProducts, searchTerm]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E3A6F]"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Category not found</h2>
          <Link to="/products">
            <Button className="bg-[#2E3A6F] hover:bg-[#1f2747]">
              Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleDownloadCatalog = () => {
    if (category.catalog_pdf) {
      const url = category.catalog_pdf.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${category.catalog_pdf}` : category.catalog_pdf;
      window.open(url, '_blank');
    } else {
      alert(`${category.name} Catalog is not available yet. Admin can upload catalog PDFs.`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white" data-testid="category-page">
      {/* Header */}
      <section className="bg-[#2E3A6F] text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          {category.image ? (
            <img
              src={category.image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${category.image}` : category.image}
              alt={category.name}
              className="w-full h-full object-cover opacity-20"
            />
          ) : (
            <div
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: "url('https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?q=80&w=2000')" }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#2E3A6F] via-[#2E3A6F]/95 to-[#2E3A6F]/90" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link to="/products" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors group">
            <ArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" size={20} />
            Back to Products
          </Link>
          <div className="max-w-3xl space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold">{category.name}</h1>
                <p className="text-lg text-white/90 mt-2">{category.description}</p>
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Button
                  size="lg"
                  className="bg-white text-[#2E3A6F] hover:bg-gray-100 transition-all duration-300 hover:shadow-xl group whitespace-nowrap"
                  onClick={handleDownloadCatalog}
                  data-testid="download-catalog-btn"
                >
                  <Download className="mr-2 group-hover:translate-y-1 transition-transform" size={20} />
                  Download {category.name} Catalog
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Subcategories */}
      <section className="py-8 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {subcategories.length > 0 && (
            <div className="mb-8 sm:mb-12">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-5 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Subcategories</h2>
                {activeSub && (
                  <button
                    onClick={() => setActiveSub(null)}
                    className="inline-flex items-center gap-1 text-xs sm:text-sm text-[#2E3A6F] hover:underline"
                    data-testid="clear-subcategory-filter"
                  >
                    <X size={14} /> Show all subcategories
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
                {subcategories.map((sub) => {
                  const isActive = activeSub === sub.id;
                  return (
                    <button
                      type="button"
                      key={sub.id}
                      onClick={() => setActiveSub(isActive ? null : sub.id)}
                      className={`text-left transition-all ${isActive ? 'ring-2 ring-[#2E3A6F]' : ''}`}
                      data-testid={`subcategory-card-${sub.id}`}
                    >
                      <Card className={`group hover:shadow-xl transition-all duration-300 border-0 cursor-pointer h-full ${isActive ? 'bg-[#2E3A6F] text-white' : 'bg-white'}`}>
                        <CardContent className="p-4 sm:p-6 space-y-2 sm:space-y-3">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center transition-colors duration-300 overflow-hidden ${isActive ? 'bg-white/15' : 'bg-[#2E3A6F]/10 group-hover:bg-[#2E3A6F]'}`}>
                            {sub.icon_url ? (
                              <img
                                src={sub.icon_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${sub.icon_url}` : sub.icon_url}
                                alt={sub.name}
                                className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                              />
                            ) : (
                              <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full ${isActive ? 'bg-white' : 'bg-[#2E3A6F] group-hover:bg-white'} transition-colors`}></div>
                            )}
                          </div>
                          <h3 className={`text-sm sm:text-lg font-semibold ${isActive ? 'text-white' : 'text-gray-900'}`}>{sub.name}</h3>
                          <p className={`text-xs sm:text-sm ${isActive ? 'text-white/80' : 'text-gray-600'}`}>{sub.product_count || 0} Products</p>
                        </CardContent>
                      </Card>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search + product list */}
          <div>
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {activeSub ? subcategories.find(s => s.id === activeSub)?.name + ' Products' : 'Products'}
                <span className="ml-2 text-sm font-normal text-gray-500">({filteredProducts.length})</span>
              </h2>
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  type="text"
                  placeholder="Search this category…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                  className="pl-9 pr-9"
                  data-testid="category-search-input"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid="clear-search">
                    <X size={14} />
                  </button>
                )}
                {/* Autosuggest dropdown */}
                {searchFocused && suggestions.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border max-h-72 overflow-y-auto" data-testid="search-suggestions">
                    {suggestions.map(s => (
                      <Link
                        key={s.id}
                        to={`/product/${s.id}`}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                        data-testid={`suggestion-${s.id}`}
                      >
                        {s.images?.[0]?.url ? (
                          <img src={s.images[0].url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${s.images[0].url}` : s.images[0].url} alt={s.name} className="w-10 h-10 object-contain bg-gray-50 rounded" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center"><Package size={16} className="text-gray-400" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{s.name}</div>
                          {s.sku && <div className="text-[10px] text-gray-500 truncate">SKU: {s.sku}</div>}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {filteredProducts.length > 0 ? (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {pagedProducts.map((product) => (
                  <Link key={product.id} to={`/product/${product.id}`} data-testid={`product-link-${product.id}`}>
                    <Card className="group hover:shadow-2xl transition-all duration-500 border-0 bg-white overflow-hidden h-full">
                      <div className="relative overflow-hidden">
                        {product.images?.length > 0 && product.images[0]?.url ? (
                          <img
                            src={product.images[0].url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${product.images[0].url}` : product.images[0].url}
                            alt={product.name}
                            className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://placehold.co/600x400/e5e7eb/6b7280?text=No+image'; }}
                          />
                        ) : (
                          <div className="w-full h-56 bg-gradient-to-br from-[#2E3A6F] to-[#1a1f3a] flex items-center justify-center">
                            <Package className="text-white/30" size={48} />
                          </div>
                        )}
                        <div className="absolute top-2 right-2 flex gap-1">
                          {product.is_featured && <span className="text-[10px] px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium">Featured</span>}
                          {product.is_new_arrival && <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">New</span>}
                        </div>
                      </div>
                      <CardContent className="p-6 space-y-4">
                        <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#2E3A6F] transition-colors">
                          {product.name}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2">{product.description}</p>
                        <div className="flex items-center text-[#2E3A6F] font-medium group-hover:translate-x-2 transition-transform duration-300">
                          View Details
                          <ArrowRight className="ml-2" size={18} />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10" data-testid="category-pagination">
                    <Button
                      variant="outline"
                      disabled={page <= 1}
                      onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      data-testid="pagination-prev"
                      className="border-[#2E3A6F]/30 text-[#2E3A6F] disabled:opacity-40"
                    >
                      <ArrowLeft size={16} className="mr-1" /> Prev
                    </Button>
                    <span className="text-sm text-gray-600 px-2" data-testid="pagination-info">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      disabled={page >= totalPages}
                      onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      data-testid="pagination-next"
                      className="border-[#2E3A6F]/30 text-[#2E3A6F] disabled:opacity-40"
                    >
                      Next <ArrowRight size={16} className="ml-1" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Card className="border-0 bg-white p-12">
                <div className="text-center space-y-4">
                  <p className="text-gray-600">
                    {searchTerm
                      ? `No products match "${searchTerm}"${activeSub ? ' in this subcategory' : ''}.`
                      : (activeSub ? 'No products in this subcategory yet.' : 'More products coming soon in this category.')}
                  </p>
                  {(searchTerm || activeSub) && (
                    <Button variant="outline" onClick={() => { setSearchTerm(''); setActiveSub(null); }} data-testid="clear-all-filters">
                      Clear filters
                    </Button>
                  )}
                  {!searchTerm && !activeSub && (
                    <Link to="/enquiry">
                      <Button className="bg-[#2E3A6F] hover:bg-[#1f2747]">
                        Enquire About Products
                      </Button>
                    </Link>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white border-t">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#f8f9fb] to-white rounded-2xl p-8 md:p-12 text-center space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">Need Help Finding the Right Tool?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our experts are here to help you select the perfect tooling solution for your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/enquiry">
                <Button size="lg" className="bg-[#2E3A6F] hover:bg-[#1f2747]">
                  Send Enquiry
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Category;
